import { type ElementSize } from "../hooks/use-element-size.ts"

// The calendar grid already says when a meeting happens, how long it lasts,
// and which day/column it's in — but the card should still surface the
// event's own relevant fields (type, time, who/what it's for) whenever the
// box has room, compressing the LAYOUT rather than deleting the data. A
// round that capped the card at title-only-below-45-minutes and at most one
// context line ever went too far the other way — reported live against a
// reference design that shows a full 3-4 line card once there's real
// height, and type+time sharing one compact row even at 15/30 minutes
// (never hidden outright). This version restores relevant content while
// keeping every fix that was actually about a real bug: the box-size-driven
// (not duration-keyed) cutoffs, the fixed typography, and the true
// `eventMinHeight` floor.
//
// Every cutoff below keys off the rendered box, not `diffInMinutes`:
// FullCalendar's `eventMinHeight` (Calendar.tsx, now a true 20px safety
// floor rather than a blanket minimum — see its own comment) and
// calendar.less' `@slot-height` are what make a real 15-minute event
// (~14px, floor-clamped to 20px) genuinely shorter than a real 30-minute
// one (28px, unclamped) — a duration cutoff could never express that
// (see the 2026-09-15 KB decision this whole file's history stems from).
const HEIGHT_FOR_STACKED_TIME = 32
const HEIGHT_FOR_ONE_CONTEXT_LINE = 48
const HEIGHT_FOR_TWO_CONTEXT_LINES = 70

// Type labels are short, controlled vocabulary ("Site Visit", "Personal",
// "Follow-Up") — nowhere near as wide as the free-typed titles this design
// used to have to defend the row against, so one width bar for "is there
// room to show time at all" covers both the shared-row (inline) and the
// stacked case; there's no separate stricter floor-only bar anymore.
const MIN_WIDTH_FOR_TIME = 64
// Width priority as the column narrows, widest requirement first (so it's
// the first thing to compress or disappear): the second context line, then
// the first, then the full time range collapsing to just its start, then
// time disappearing outright. Type never loses this race — ellipsis is its
// own last-resort safety net, never triggered by these cutoffs.
const MIN_WIDTH_FOR_CONTEXT_LINE = 100
const MIN_WIDTH_FOR_TIME_RANGE = 110
// A narrow overlapping column reads better as one compact row than as a
// stack squeezed into little width — time collapses back onto the type's
// own line rather than getting a stacked line of its own, however tall the
// box is.
const MIN_WIDTH_FOR_STACKED_TIME = 90

export type TimePlacement = "inline" | "stacked" | "hidden"
export type TimeContent = "full" | "short"

export interface EventCardTier {
  timePlacement: TimePlacement
  /** Only meaningful when `timePlacement !== "hidden"`. */
  timeContent: TimeContent
  showContext: boolean
  /** A second context line, past the first — real room to spare only. */
  showSecondContext: boolean
}

export function pickEventCardTier({ width, height }: ElementSize): EventCardTier {
  const canStackHeight = height >= HEIGHT_FOR_STACKED_TIME
  const canStackWidth = width >= MIN_WIDTH_FOR_STACKED_TIME
  const stacked = canStackHeight && canStackWidth

  const hasTimeRoom = width >= MIN_WIDTH_FOR_TIME
  const timePlacement: TimePlacement = !hasTimeRoom
    ? "hidden"
    : stacked
      ? "stacked"
      : "inline"

  const showContext =
    width >= MIN_WIDTH_FOR_CONTEXT_LINE && height >= HEIGHT_FOR_ONE_CONTEXT_LINE

  return {
    timePlacement,
    timeContent: width >= MIN_WIDTH_FOR_TIME_RANGE ? "full" : "short",
    showContext,
    // Rides on the first line existing at all — a box too short/narrow for
    // one context line never gets a second just because it's independently
    // tall (the height-for-two check is a superset of the height-for-one
    // check, so this only ever adds, never substitutes).
    showSecondContext: showContext && height >= HEIGHT_FOR_TWO_CONTEXT_LINES
  }
}

// The first half of `eventInfo.timeText` (FullCalendar's own formatted
// "11:00am - 11:30am"), used when there's only room for one time — showing
// when a meeting starts still answers "when is it" without the full
// range's width.
export function timeRangeStart(timeText: string): string {
  const [start] = timeText.split(/\s[-–]\s/)
  return (start || timeText).trim()
}

// calendar.less' `@slot-height` (28px per 30-minute slot — the grid's own
// pixel-per-minute scale, untouched) and Calendar.tsx's `eventMinHeight`
// floor, mirrored here for the one case a real measurement isn't available
// yet: initial mount, and jsdom in tests, which never runs layout at all.
const SLOT_HEIGHT_PX = 28
const SLOT_MINUTES = 30
const MIN_EVENT_HEIGHT_PX = 20

export function estimateEventHeightPx(durationMinutes: number): number {
  if (!Number.isFinite(durationMinutes)) return MIN_EVENT_HEIGHT_PX

  return Math.max(
    MIN_EVENT_HEIGHT_PX,
    (durationMinutes / SLOT_MINUTES) * SLOT_HEIGHT_PX
  )
}
